import React, { useCallback } from 'react';

import { IntegrationDomain, IntegrationVersion } from 'api/usePublisherIntegrationTest';

import { IntegrationTestModal } from 'components/IntegrationTestModal/IntegrationTestModal';

import { docsUrls } from '../DeveloperSettings.constants';
import { IIntegrationSectionDependencies } from '../DeveloperSettings.integration';
import { EApiVersion, ESavingForm } from '../DeveloperSettings.types';
import { IVersionConfig } from '../DeveloperSettings.types';
import { useApiTestingAnalytics } from '../hooks/useApiTestingAnalytics';
import { usePersonalizeForm } from '../hooks/useCallbackForms';
import { useIntegrationTestModal } from '../hooks/useIntegrationTestModal';

import { CallbackSection } from './CallbackSection';

type IPersonalizeSectionProps = IIntegrationSectionDependencies;

export const PersonalizeSection: React.FC<IPersonalizeSectionProps> = ({
  publisherId,
  integrationData,
  updateIntegration,
  getIntegration,
  savingForm,
  setSavingForm,
  isProfilesEnabled,
  hasCategories,
  isWebStore,
  setTaskStatus
}) => {
  const analytics = useApiTestingAnalytics({
    domain: IntegrationDomain.PERSONALIZATION,
    version: IntegrationVersion.V1
  });

  const form = usePersonalizeForm({
    integrationData,
    updateIntegration,
    getIntegration,
    hasCategories,
    isWebStore,
    setTaskStatus,
    setSavingForm,
    onSaveResult: analytics.handleSaveResult
  });

  const callbackUrl = form.initialValues.playerInfoSyncUrl;

  const testModal = useIntegrationTestModal({
    publisherId,
    domain: IntegrationDomain.PERSONALIZATION,
    version: IntegrationVersion.V1,
    title: 'Personalize Web Store Callback Test',
    callbackUrl,
    onTestResult: analytics.trackTestExecuted
  });

  const { openModal } = testModal;

  const handleOpenModal = useCallback(() => {
    analytics.trackTestModalOpened({ url: callbackUrl });
    openModal();
  }, [analytics, callbackUrl, openModal]);

  const getPersonalizeError = (): string | undefined => {
    if (form.touched.playerInfoSyncUrl) {
      return form.errors.playerInfoSyncUrl as string;
    }
    return undefined;
  };

  const versionConfigs: IVersionConfig[] = [
    {
      version: EApiVersion.V1,
      label: EApiVersion.V1,
      value: form.values.playerInfoSyncUrl,
      onChange: (value) => form.setFieldValue('playerInfoSyncUrl', value),
      error: getPersonalizeError(),
      docsUrl: docsUrls.personalizeWebstore,
      urlDataTestId: 'personalize-callback-url'
    }
  ];

  const { schema, ...testModalProps } = testModal;

  return (
    <>
      <CallbackSection
        title="Personalize Web Store Callback"
        versionConfigs={versionConfigs}
        onSave={form.submitForm}
        isSaveDisabled={!form.dirty || savingForm !== ESavingForm.NONE}
        isLoading={savingForm === ESavingForm.PERSONALIZE}
        disabled={isProfilesEnabled}
        saveButtonDataTestId="personalize-save"
        onTestClick={handleOpenModal}
        onDocsClick={analytics.trackDocsClicked}
      />
      {schema && (
        <IntegrationTestModal {...testModalProps} schema={schema} />
      )}
    </>
  );
};
